import type { ResidentDTO } from './resident.types';

export enum AdmissionStatus {
    INQUIRY = 'INQUIRY',
    TOURING = 'TOURING',
    WAITLIST = 'WAITLIST',
    ADMITTED = 'ADMITTED',
    LOST = 'LOST',
}

export enum CarePriority {
    URGENT = 'URGENT',
    HIGH = 'HIGH',
    MEDIUM = 'MEDIUM',
    LOW = 'LOW',
}

export enum CareStatus {
    NEW = 'NEW',
    PENDING = 'PENDING',
    ASSIGNED = 'ASSIGNED',
    IN_PROGRESS = 'IN_PROGRESS',
    HOLD = 'HOLD',
    RESOLVED = 'RESOLVED',
    CLOSED = 'CLOSED',
}

export enum CareDocumentType {
    MEDICAL_RECORD = 'MEDICAL_RECORD',
    CONSENT_FORM = 'CONSENT_FORM',
    INSURANCE = 'INSURANCE',
    ADMISSION_AGREEMENT = 'ADMISSION_AGREEMENT',
}

export interface ProspectiveResidentDTO {
    id: string;
    name: string;
    responsibleParty: string;
    email?: string;
    phone?: string;
    source: string;
    status: AdmissionStatus;
    notes?: string;
    readinessScore?: number;
    createdAt?: string;
    updatedAt?: string;
}

export interface CareRequestDTO {
    id: string;
    requestNumber: string;
    residentId: string;
    residentName?: string;
    unitId?: string;
    description: string;
    priority: CarePriority;
    category: string;
    subcategory: string;
    status: CareStatus;
    assignedTo?: string;
    assignedToName?: string;
    requestedBy?: string;
    requestedById?: string;
    requestedByName?: string;
    residentNeed?: string;
    createdByName?: string;
    updatedByName?: string;
    slaDeadline: string;
    isEscalated?: boolean;
    closedRemark?: string;
    resolutionNote?: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface CareDashboardStats {
    totalActiveRequests: number;
    pendingRequests: number;
    criticalCareAlerts: number;
    admissionsInPipeline?: number;
    staffWorkload?: Record<string, number>;
}

export interface CareDocumentDTO {
    id: string;
    title: string;
    type: CareDocumentType;
    fileSize: string;
    createdAt: string;
    uploadedBy?: string;
}

export interface CareSLAReportDTO {
    month: string;
    targetMet: number;
    targetBreached: number;
    avgResolutionTime: number;
}

export interface CareStaffAssignmentDTO {
    id: string;
    residentName: string;
    unitLabel: string;
    staffName: string;
    taskDescription: string;
    scheduledAt: string;
    status: 'SCHEDULED' | 'ON_CALL' | 'COMPLETED' | 'CANCELLED';
}

export interface ShiftHandoverDTO {
    id: string;
    residentId: string;
    residentName?: string;
    roomNumber?: string;
    situation?: string;
    background?: string;
    assessment?: string;
    recommendation?: string;
    safetyAlerts?: string[];
    isCritical?: boolean;
    status?: string;
    summary?: string;
    mood?: boolean;
    vitals?: { bp: string; temp: string; pulse: string; spO2: string };
    checklist?: { task: string; completed: boolean }[];
    taskCategories?: {
        key: string;
        label: string;
        color: string;
        tasks: { id: string; label: string; completed: boolean }[];
    }[];
    handedOverAt?: string;
    createdAt?: string;
    updatedAt?: string;
    notes?: string;
    handoverNote?: string;

    // Backend mapped fields
    dailyBloodPressure?: string;
    dailyBloodSugar?: string;
    dailyWeight?: string;
    temperature?: string;
    pulseRate?: string;
    respirationRate?: string;
    spO2?: string;
    painLevel?: string;
    levelOfConsciousness?: string;
    mealPortion?: string;
    bowelMovement?: string;
    oxygenLevel?: string;
    bowel?: boolean;
    urinary?: boolean;
    catheterDrain?: boolean;
    incontinenceCare?: boolean;
    shampooDash?: boolean;
    partialBath?: boolean;
    shave?: boolean;
    combHair?: boolean;
    handNailCare?: boolean;
    toeNailCare?: boolean;
    applySkinBarrier?: boolean;
    pedicare?: boolean;
    lowerExtremities?: boolean;
    applyLotion?: boolean;
    elevateLeg?: boolean;
    bedMeal?: boolean;
    medicationGiven?: boolean;
    nightCheckup?: boolean;
    snackWater?: boolean;
    fullBath?: boolean;
    oralCare?: boolean;
    assistWithClothing?: boolean;
    breakfast?: boolean;
    lunch?: boolean;
    dinner?: boolean;

    history?: {
        userId: string;
        userName: string;
        updatedAt: string;
    }[];
    completionRatio?: string;
}

export type { ResidentDTO };
